import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import "./style.css";
import MediaViewer from "../MediaViewer";
import { messengerService } from "../../../functions/messenger";

const MIN_ZOOM = 1;
const MAX_ZOOM = 4;
const ZOOM_STEP = 0.25;

export default function ChatHeader({
  conversation,
  groupPictureOverride,
  currentUser,
  messages,
  // onStartVoiceCall,
  // onStartVideoCall,
  onBack,
  onSearchInConversation,
  onToggleMute,
  onShowCallHistory,
  onThemeSettings,
  onDeleteConversation,
  onClearConversation,
  onBlockUser,
  onUnblockUser,
  isBlocked,
  onReportUser,
  onViewProfile,
  onOpenGroupInfo,
  onShareContact,
  // onExportChat,
  onArchiveConversation,
  onConversationPinToggle,
  // Selection mode
  isSelecting,
  selectedCount,
  onStartSelectMessages,
  onCancelSelectMessages,
  onStarSelected,
  onUnstarSelected,
  onCopySelected,
  onDeleteSelected,
  allSelectedAreStarred,
  canDeleteForEveryone,
  // Selection: message info
  canShowSelectedMessageInfo,
  onShowSelectedMessageInfo,
  onlineUsers,
}) {
  const [showOptions, setShowOptions] = useState(false);
  const [showMediaViewer, setShowMediaViewer] = useState(false);
  const [showBlockConfirmation, setShowBlockConfirmation] = useState(false);
  const [showPhotoViewer, setShowPhotoViewer] = useState(false);
  const [viewerZoom, setViewerZoom] = useState(MIN_ZOOM);
  const [viewerOffset, setViewerOffset] = useState({ x: 0, y: 0 });
  // const [isCallInProgress, setIsCallInProgress] = useState(false);
  const optionsRef = useRef(null);
  const dragStateRef = useRef({
    dragging: false,
    startX: 0,
    startY: 0,
    originX: 0,
    originY: 0,
  });

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (optionsRef.current && !optionsRef.current.contains(event.target)) {
        setShowOptions(false);
      }
    };

    if (showOptions) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showOptions]);

  // Removed call in progress effect

  const getConversationName = () => {
    if (conversation.type === 'group') {
      return conversation.name || 'Group Chat';
    }
    
    const otherUser = conversation.participants.find(p => p._id !== currentUser.id);
    return otherUser ? `${otherUser.first_name} ${otherUser.last_name}` : 'Unknown User';
  };

  const getOtherUser = () => {
    if (!conversation?.participants) return null;
    const currentUserId = String(currentUser?.id || currentUser?._id || '');
    return conversation.participants.find((p) => {
      const participantId = String(p?._id || p?.id || p);
      return participantId !== currentUserId;
    }) || null;
  };

  const getConversationPicture = () => {
    if (conversation.type === 'group') {
      return groupPictureOverride || conversation.groupPicture || conversation.picture || '/images/default_group.svg';
    }

    const otherUser = getOtherUser();
    return otherUser?.picture || '/images/default_profile.png';
  };

  const closePhotoViewer = useCallback(() => {
    setShowPhotoViewer(false);
    setViewerZoom(MIN_ZOOM);
    setViewerOffset({ x: 0, y: 0 });
  }, []);

  const openPhotoViewer = () => {
    if (conversation.type === 'group') return;
    setViewerZoom(MIN_ZOOM);
    setViewerOffset({ x: 0, y: 0 });
    setShowPhotoViewer(true);
  };

  const zoomBy = useCallback((delta) => {
    setViewerZoom((prev) =>
      Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Number((prev + delta).toFixed(2))))
    );
  }, []);

  const handleViewerWheel = (e) => {
    e.preventDefault();
    zoomBy(e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP);
  };

  const handleViewerPointerDown = (e) => {
    if (viewerZoom <= MIN_ZOOM) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    dragStateRef.current = {
      dragging: true,
      startX: e.clientX,
      startY: e.clientY,
      originX: viewerOffset.x,
      originY: viewerOffset.y,
    };
  };

  const handleViewerPointerMove = (e) => {
    const drag = dragStateRef.current;
    if (!drag.dragging) return;
    setViewerOffset({
      x: drag.originX + (e.clientX - drag.startX),
      y: drag.originY + (e.clientY - drag.startY),
    });
  };

  const handleViewerPointerUp = () => {
    dragStateRef.current.dragging = false;
  };

  const handleViewerDoubleClick = () => {
    if (viewerZoom > MIN_ZOOM) {
      setViewerZoom(MIN_ZOOM);
      setViewerOffset({ x: 0, y: 0 });
    } else {
      setViewerZoom(2);
    }
  };

  useEffect(() => {
    if (viewerZoom <= MIN_ZOOM) {
      setViewerOffset({ x: 0, y: 0 });
    }
  }, [viewerZoom]);

  useEffect(() => {
    if (!showPhotoViewer) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") closePhotoViewer();
      if (e.key === "+" || e.key === "=") zoomBy(ZOOM_STEP);
      if (e.key === "-" || e.key === "_") zoomBy(-ZOOM_STEP);
      if (e.key === "0") {
        setViewerZoom(MIN_ZOOM);
        setViewerOffset({ x: 0, y: 0 });
      }
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [showPhotoViewer, zoomBy, closePhotoViewer]);

  useEffect(() => {
    closePhotoViewer();
  }, [conversation?._id, closePhotoViewer]);

  // Memoize online status to avoid recalculating on every render
  const onlineStatus = useMemo(() => {
    if (conversation.type === 'group') {
      return null; // Groups don't have online status
    }
    
    // Find the other user (not the current user)
    const otherUser = conversation.participants?.find(p => {
      const participantId = String(p._id || p.id || p);
      const currentUserId = String(currentUser.id || currentUser._id || currentUser);
      return participantId !== currentUserId;
    });
    
    if (!otherUser) {
      return 'Offline';
    }

    // Silent block: if the current user blocked this person, never reveal
    // their presence. (The blocked party's client receives no presence events
    // for us either, so this stays symmetric without exposing any label.)
    if (isBlocked) {
      return 'Offline';
    }
    
    // Check if onlineUsers exists and is valid
    if (!onlineUsers) {
      return 'Offline';
    }
    
    // Convert onlineUsers to array (handle both Set and Array)
    const onlineUsersArray = onlineUsers instanceof Set 
      ? Array.from(onlineUsers) 
      : Array.isArray(onlineUsers) 
        ? onlineUsers 
        : [];
    
    if (onlineUsersArray.length === 0) {
      return 'Offline';
    }
    
    // Helper function to normalize IDs to strings (handles ObjectId objects)
    const normalizeId = (id) => {
      if (id === null || id === undefined) return '';
      // Handle MongoDB ObjectId
      if (typeof id === 'object' && id !== null) {
        if (typeof id.toString === 'function') {
          return id.toString();
        }
        // If it's an object with _id or id property
        if (id._id) {
          return normalizeId(id._id);
        }
        if (id.id) {
          return normalizeId(id.id);
        }
      }
      return String(id);
    };
    
    // Get the other user's ID - normalize to string
    const otherUserId = normalizeId(otherUser._id || otherUser.id || otherUser);
    
    // Check if the other user is in the online users list
    const isOnline = onlineUsersArray.some(onlineId => {
      const normalizedOnlineId = normalizeId(onlineId);
      return normalizedOnlineId === otherUserId;
    });
    
    return isOnline ? 'Online' : 'Offline';
  }, [conversation, currentUser, onlineUsers, isBlocked]);

  // Removed voice and video call handlers

  const showNotification = (message, type = 'info') => {
    const notification = document.createElement('div');
    notification.textContent = message;
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: ${type === 'warning' ? '#ffd43b' : type === 'error' ? '#ff6b6b' : '#51cf66'};
      color: ${type === 'warning' ? '#000' : 'white'};
      padding: 12px 20px;
      border-radius: 8px;
      z-index: 10000;
      font-size: 14px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.2);
      animation: slideInRight 0.3s ease-out;
    `;
    
    document.body.appendChild(notification);
    
    setTimeout(() => {
      if (notification.parentNode) {
        notification.style.animation = 'slideOutRight 0.3s ease-out';
        setTimeout(() => {
          if (notification.parentNode) {
            notification.parentNode.removeChild(notification);
          }
        }, 300);
      }
    }, 3000);
  };

  const handleBlockUser = () => {
    if (conversation.type === 'group') {
      showNotification('Cannot block group conversations', 'error');
      return;
    }

    const otherUser = conversation.participants.find(p => p._id !== currentUser.id);
    if (otherUser) {
      setShowBlockConfirmation(true);
    }
    setShowOptions(false);
  };

  const handleConfirmBlockUser = () => {
    const otherUser = conversation.participants.find(p => p._id !== currentUser.id);
    if (otherUser) {
      onBlockUser && onBlockUser(otherUser._id);
      showNotification(`${otherUser.first_name} has been blocked`, 'info');
    }
    setShowBlockConfirmation(false);
  };

  const handleCancelBlockUser = () => {
    setShowBlockConfirmation(false);
  };

  const handleReportUser = () => {
    if (conversation.type === 'group') {
      showNotification('Cannot report group conversations', 'error');
      return;
    }
    
    const otherUser = conversation.participants.find(p => p._id !== currentUser.id);
    if (otherUser) {
      const reason = prompt(`Please provide a reason for reporting ${otherUser.first_name}:`);
      if (reason) {
        onReportUser && onReportUser(otherUser._id, reason);
        showNotification('User has been reported', 'info');
      }
    }
    setShowOptions(false);
  };

  const handleViewProfile = () => {
    if (conversation.type === 'group') {
      showNotification('Cannot view profile for group conversations', 'error');
      return;
    }
    
    const otherUser = conversation.participants.find(p => p._id !== currentUser.id);
    if (otherUser) {
      onViewProfile && onViewProfile(otherUser._id);
    }
    setShowOptions(false);
  };

  // New functions for chat user info options
  const handleMediaAndDocs = () => {
    // Pass conversationId and token to MediaViewer so it can fetch all messages including deleted ones
    setShowMediaViewer(true);
    // Close any open menus
    setShowOptions(false);
  };

  // Unused helpers removed

  const handleSelectMessages = () => {
    if (onStartSelectMessages) {
      onStartSelectMessages();
    }
    setShowOptions(false);
  };

  // const handleExportChat = () => {
  //   onExportChat && onExportChat(conversation._id);
  //   showNotification('Chat export started', 'info');
  //   setShowOptions(false);
  // };

  // const handleArchiveConversation = () => {
  //   onArchiveConversation && onArchiveConversation(conversation._id);
  //   showNotification('Conversation archived', 'info');
  //   setShowOptions(false);
  // };

  // Selection mode header UI
  if (isSelecting) {
    const canDelete = selectedCount > 0;
    return (
      <div className="chat_header selection_mode">
        <div className="chat_header_left">
          <button
            className="chat_back_btn"
            onClick={onCancelSelectMessages}
            title="Cancel selection"
            aria-label="Cancel selection"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
              <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
            </svg>
          </button>
          <div className="chat_user_details">
            <h3 className="chat_user_name">{selectedCount} selected</h3>
          </div>
        </div>
        <div className="chat_actions selection_actions">
          <button
            className="chat_action_btn"
            title={allSelectedAreStarred ? "Unstar selected" : "Star selected"}
            aria-label={allSelectedAreStarred ? "Unstar selected" : "Star selected"}
            onClick={allSelectedAreStarred ? onUnstarSelected : onStarSelected}
            disabled={selectedCount === 0}
          >
            {allSelectedAreStarred ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
              </svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/>
              </svg>
            )}
          </button>
          {canShowSelectedMessageInfo && (
            <button
              className="chat_action_btn"
              title="Message info"
              aria-label="Message info"
              onClick={onShowSelectedMessageInfo}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
              </svg>
            </button>
          )}
          <button
            className="chat_action_btn"
            title="Copy selected"
            aria-label="Copy selected"
            onClick={onCopySelected}
            disabled={selectedCount === 0}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
            </svg>
          </button>
          {onDeleteSelected && (
            <button
              className="chat_action_btn selection_delete_btn"
              title={canDeleteForEveryone ? "Delete selected" : "Delete for me"}
              aria-label="Delete selected messages"
              onClick={onDeleteSelected}
              disabled={!canDelete}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
              </svg>
            </button>
          )}
          <button
            className="chat_action_btn selection_cancel_btn"
            title="Cancel"
            aria-label="Cancel selection"
            onClick={onCancelSelectMessages}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="chat_header">
      <div className="chat_header_left">
        <button
          className="chat_back_btn"
          onClick={onBack}
          title="Back to conversations"
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
            <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
          </svg>
        </button>
        
        <div
          className={`chat_user_info${conversation.type !== 'group' ? ' is_profile_clickable' : ''}`}
          onClick={() => {
            if (conversation.type !== 'group') {
              openPhotoViewer();
            }
          }}
          title={conversation.type !== 'group' ? 'View profile picture' : undefined}
        >
          <img
            src={getConversationPicture()}
            alt={getConversationName()}
            className="chat_user_picture"
          />
          <div className="chat_user_details">
            {conversation.type === 'group' ? (
              <button
                type="button"
                className="chat_user_name chat_user_name_btn"
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenGroupInfo && onOpenGroupInfo();
                }}
                title="View group info"
              >
                {getConversationName()}
              </button>
            ) : (
              <h3 className="chat_user_name">{getConversationName()}</h3>
            )}
            {conversation.type === 'private' && onlineStatus && (
              <span className={`chat_user_status ${onlineStatus === 'Online' ? 'online' : 'offline'}`}>
                {onlineStatus}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="chat_actions">
        {/* Main Options Menu Button */}
        <button
          className="chat_action_btn options_menu_btn"
          onClick={() => setShowOptions(!showOptions)}
          title="More options"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
          </svg>
        </button>
        {/* Main Options Menu */}
        {showOptions && (
          <div className="chat_options_menu" ref={optionsRef}>
            {conversation.type !== 'group' && (
              <>
                <button className="chat_option_item" onClick={handleViewProfile}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                  </svg>
                  View profile
                </button>
                <button className="chat_option_item" onClick={handleMediaAndDocs}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
                  </svg>
                  Media, links, and docs
                </button>
                <div className="menu_divider"></div>
              </>
            )}
            <button className="chat_option_item" onClick={() => { handleSelectMessages(); }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
              </svg>
              Select messages
            </button>
            {/* <button className="chat_option_item" onClick={() => {
              onSearchInConversation && onSearchInConversation();
              setShowOptions(false);
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
              </svg>
              Search in conversation
            </button> */}
            <button className="chat_option_item" onClick={() => {
              // Check if conversation is already muted
              if (conversation.muted) {
                // Unmute the conversation
                conversation.muted = false;
                conversation.mutedUntil = null;
                conversation.muteDuration = null;
                showNotification('Conversation unmuted', 'info');
              } else {
                // Show mute duration modal
                onToggleMute && onToggleMute();
              }
              setShowOptions(false);
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M16 8c0-2.21-1.79-4-4-4S8 5.79 8 8c0 .55.45 1 1 1s1-.45 1-1c0-1.1.9-2 2-2s2 .9 2 2-.9 2-2 2c-.55 0-1 .45-1 1v2c0 .55.45 1 1 1s1-.45 1-1v-1.43c1.16-.41 2-1.52 2-2.57zm-4 8c.55 0 1-.45 1-1s-.45-1-1-1-1 .45-1 1 .45 1 1 1z"/>
              </svg>
              {conversation.muted ? 'Unmute notifications' : 'Mute notifications'}
            </button>
            <button className="chat_option_item" onClick={async () => {
              try {
                const isPinned = conversation.pinned || false;
                if (isPinned) {
                  // Unpin conversation
                  await messengerService.unpinConversation(conversation._id, currentUser.token);
                  conversation.pinned = false;
                  showNotification('Conversation unpinned', 'info');
                } else {
                  // Pin conversation
                  await messengerService.pinConversation(conversation._id, currentUser.token);
                  conversation.pinned = true;
                  showNotification('Conversation pinned', 'info');
                }
                
                // Update conversation in parent component state
                if (onConversationPinToggle) {
                  onConversationPinToggle(conversation._id, !isPinned);
                }
              } catch (error) {
                console.error('Error toggling pin status:', error);
                showNotification('Failed to update pin status. Please try again.', 'error');
              }
              setShowOptions(false);
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M16,12V4H17V2H7V4H8V12L6,14V16H11.2V22H12.8V16H18V14L16,12M8.8,14L10,12.8V4H14V12.8L15.2,14H8.8Z"/>
              </svg>
              {conversation.pinned ? 'Unpin conversation' : 'Pin conversation'}
            </button>
            <button className="chat_option_item" onClick={() => {
              onThemeSettings && onThemeSettings();
              setShowOptions(false);
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12,3c-4.97,0-9,4.03-9,9s4.03,9,9,9c0.83,0,1.5-0.67,1.5-1.5c0-0.39-0.15-0.74-0.39-1.01c-0.23-0.26-0.38-0.61-0.38-0.99 c0-0.83,0.67-1.5,1.5-1.5H16c2.76,0,5-2.24,5-5C21,7.03,16.97,3,12,3z M6.5,12c-0.83,0-1.5-0.67-1.5-1.5S5.67,9,6.5,9 S8,9.67,8,10.5S7.33,12,6.5,12z M9.5,8C8.67,8,8,7.33,8,6.5S8.67,5,9.5,5S11,5.67,11,6.5S10.33,8,9.5,8z M14.5,8 c-0.83,0-1.5-0.67-1.5-1.5S13.67,5,14.5,5S16,5.67,16,6.5S15.33,8,14.5,8z M17.5,12c-0.83,0-1.5-0.67-1.5-1.5S16.67,9,17.5,9 S19,9.67,19,10.5S18.33,12,17.5,12z"/>
              </svg>
              Chat Theme
            </button>
            {/* <button className="chat_option_item" onClick={handleExportChat}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
              </svg>
              Export chat
            </button> */}
            {/* <button className="chat_option_item" onClick={handleArchiveConversation}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.54 5.23l-1.39-1.68C18.88 3.21 18.47 3 18 3H6c-.47 0-.88.21-1.16.55L3.46 5.23C3.17 5.57 3 6.02 3 6.5V19c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6.5c0-.48-.17-.93-.46-1.27zM12 17.5l-4-4h2.5V12h3v1.5H16l-4 4z"/>
              </svg>
              Archive conversation
            </button> */}
            <button className="chat_option_item" onClick={() => {
              onClearConversation && onClearConversation();
              setShowOptions(false);
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 13H5v-2h14v2z"/>
              </svg>
              Clear conversation
            </button>
            {conversation.type !== 'group' && (
              <>
                <div className="menu_divider"></div>
                {isBlocked ? (
                  <button className="chat_option_item" onClick={() => {
                    const otherUser = conversation.participants.find(p => p._id !== currentUser.id);
                    if (otherUser) {
                      onUnblockUser && onUnblockUser(otherUser._id);
                      showNotification('User has been unblocked', 'info');
                    }
                    setShowOptions(false);
                  }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                    </svg>
                    Unblock user
                  </button>
                ) : (
                  <button className="chat_option_item danger" onClick={handleBlockUser}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                    </svg>
                    Block user
                  </button>
                )}
                <button className="chat_option_item danger" onClick={handleReportUser}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
                  </svg>
                  Report user
                </button>
              </>
            )}
          </div>
        )}
      </div>
      
      {showMediaViewer && (
        <MediaViewer 
          conversationId={conversation._id}
          token={currentUser.token}
          onClose={() => setShowMediaViewer(false)} 
        />
      )}
      {showBlockConfirmation && (
        <div className="block_confirm_overlay" onClick={handleCancelBlockUser}>
          <div className="block_confirm_modal" onClick={(e) => e.stopPropagation()}>
            <h3>Confirm Block</h3>
            <p>Are you sure you want to block {conversation.participants.find(p => p._id !== currentUser.id)?.first_name}?</p>
            <div className="block_confirm_actions">
              <button className="blue_btn" onClick={handleConfirmBlockUser}>Block</button>
              <button className="gray_btn" onClick={handleCancelBlockUser}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      {showPhotoViewer &&
        conversation.type !== 'group' &&
        createPortal(
          <div
            className="group_photo_viewer_overlay"
            role="dialog"
            aria-modal="true"
            aria-label="Profile photo viewer"
            onClick={closePhotoViewer}
          >
            <button
              type="button"
              className="group_photo_viewer_close"
              onClick={closePhotoViewer}
              aria-label="Close photo viewer"
            >
              ✕
            </button>

            <div
              className="group_photo_viewer_toolbar"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="group_photo_viewer_zoom_btn"
                onClick={() => zoomBy(-ZOOM_STEP)}
                disabled={viewerZoom <= MIN_ZOOM}
                aria-label="Zoom out"
                title="Zoom out"
              >
                −
              </button>
              <span className="group_photo_viewer_zoom_label">
                {Math.round(viewerZoom * 100)}%
              </span>
              <button
                type="button"
                className="group_photo_viewer_zoom_btn"
                onClick={() => zoomBy(ZOOM_STEP)}
                disabled={viewerZoom >= MAX_ZOOM}
                aria-label="Zoom in"
                title="Zoom in"
              >
                +
              </button>
              <button
                type="button"
                className="group_photo_viewer_zoom_btn group_photo_viewer_reset_btn"
                onClick={() => {
                  setViewerZoom(MIN_ZOOM);
                  setViewerOffset({ x: 0, y: 0 });
                }}
                disabled={viewerZoom <= MIN_ZOOM}
                aria-label="Reset zoom"
                title="Reset zoom"
              >
                Reset
              </button>
            </div>

            <div
              className={`group_photo_viewer_stage ${
                viewerZoom > MIN_ZOOM ? "is_zoomed" : ""
              }`}
              onClick={(e) => e.stopPropagation()}
              onWheel={handleViewerWheel}
              onPointerDown={handleViewerPointerDown}
              onPointerMove={handleViewerPointerMove}
              onPointerUp={handleViewerPointerUp}
              onPointerCancel={handleViewerPointerUp}
              onDoubleClick={handleViewerDoubleClick}
            >
              <img
                src={getConversationPicture()}
                alt={getConversationName()}
                className="group_photo_viewer_img"
                draggable={false}
                style={{
                  transform: `translate(${viewerOffset.x}px, ${viewerOffset.y}px) scale(${viewerZoom})`,
                }}
              />
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};